import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import {
  getFeatures,
  getMarketAssociations,
  getMarkets,
  getWorkItems,
  setActiveWorkItem,
  resetProductData,
  setResetDataFlag,
  setNewWorkItem,
  resetIsDataUpdated,
  resetSaveStatusOfFeatures,
  resetBatchSaveStatus,
  createEffectivity,
  createWorkItem,
} from '../../store/states/procos';
import {
  getMarketRuleRequest,
  getSaveEffectivityRequest,
  getSaveRulesRequest,
  handleBatchingForCreateRuleAPI,
} from '../../services/ProcosHelperFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '../../store/AppStore';
import {
  Master,
  ECommonDialogType,
  EDataTableType,
  EFeatureStatus,
  EFieldSize,
  EMessageType,
  EPropertyGroup,
  ERecordLength,
  EWorkItemStatus,
  ProcosTabs,
  EPackageType,
} from '../../data/Constants';
import ButtonSection from './ButtonSection';
import AutocompleteInput from '../InputComponents/AutocompleteInput';
import { FeaturesMarketAssociation } from './FeaturesMarketAssociation';
import { ProCosDataTable } from './ProcosDataTable';
import { useNavigate } from 'react-router';
import { openDialog } from '../../store/states/DialogSlice';
import { Trans, useTranslation } from 'react-i18next';
import { TabDrawer } from '../TabDrawer';
import { createRows } from '../../services/ProcosDataTableHelper';
import { RefreshButton } from '../../components/IconTypes';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import { CustomButton } from '../CustomButton';
import CategoryIcon from '@mui/icons-material/Category';
import PermDataSettingIcon from '@mui/icons-material/PermDataSetting';
import { ProcosApi } from '../../api/ProcosAPi';
import { ExportApi } from '../../api/ExportApi';
import { showToastMessage } from '../../services/ToastHelper';
import { TFunction } from 'i18next';
import { Excel } from '../../services/SvgIcons';

import type { IDialogFor, IEffectivityResult, IMarketRow, IWorkItemOption } from '../../../types';

/**
 *
 * @param {IWorkItemOption} workItem workItem is work item created
 * @param t is used for translation from i18next framework
 * @returns {React.JSX.Element} returns React Toast Message element
 */
export const showWorkItemExecutedToastToUser = (workItem: IWorkItemOption | null) => {
  const message = (
    <Trans
      i18nKey="messages.submit.success"
      values={{ workItemName: workItem?.name }}
      components={{ 1: <b /> }}
    />
  );
  showToastMessage(EMessageType.Success, message, false);
};

/**
 *
 * @param {IEffectivityResult} effectivityResult is a state for result of effectivity API
 * @param t is used for translation from i18next framework
 * @returns {React.JSX.Element} returns React Toast Message element
 */
export const showEffectivityExecutedToastToUser = (
  effectivityResult: IEffectivityResult,
  t: TFunction
) => {
  const hasFailedEffectivity = Object.keys(effectivityResult || {}).includes(
    'AllEffectivityFailed'
  );
  const isEffectivitySaved =
    effectivityResult && Object.keys(effectivityResult).length === 0 && !hasFailedEffectivity;
  const hasFailedEffectivityObject = effectivityResult && Object.keys(effectivityResult).length > 0;

  const message =
    effectivityResult && Object.keys(effectivityResult).length > 0 ? (
      <Box>
        {t('messages.submit.failedEffectivities')}
        <div className="fail-effectivity">
          {Object.keys(effectivityResult)
            .map((key) =>
              hasFailedEffectivity ? `${t('messages.submit.AllEffectivityFailed')}` : ` ${t(key)}`
            )
            .join(', ')}
        </div>
      </Box>
    ) : (
      <></>
    );
  if (isEffectivitySaved) {
    showToastMessage(EMessageType.Success, t('messages.submit.EffectivitySaved'), false);
  }
  if (hasFailedEffectivityObject) {
    showToastMessage(EMessageType.Error, message, false, false);
  }
};

/**
 * This component renders the Work Item Dropdown and Data Table which returns the MA for Products
 * @returns {JSX.Element} a  Procos component.
 */
const Procos = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [rows, setRows] = useState<IMarketRow[]>([]);
  const [pageCount, setPageCount] = useState(1);
  const [limit, setLimit] = useState(ERecordLength.RL100);
  const [page, setPage] = useState(1);
  const [confirmWiWarning, setConfirmWiWarning] = useState({
    open: false,
    title: 'dialogBox.unsavedChanges',
    description: <></>,
    alertFor: ECommonDialogType.HomeClickWarning,
  });
  const [isSaveCalled, setIsSaveCalled] = useState(false);

  const procos = useSelector((state: AppState) => state.procos);
  const {
    activeWorkItem: workItem,
    product,
    markets,
    activeWorkItem,
    workItems,
    isDataUpdated,
    isDataReset,
    newWorkItem,
  } = useSelector((state: AppState) => state.procos);
  const productCode = product.id;

  const isFeaturesPage = globalThis.location.href.includes('features');

  //trigger the  API to fetch the markets,markeassociation,workitems response
  //Will be triggered when creating a new work item (Saving from master) or when switching between work items
  const fetchData = async () => {
    const workItemId = activeWorkItem && activeWorkItem?.id !== Master.id ? activeWorkItem.id : '';
    const promises = [];

    if (product?.id) {
      // Do not dispatch getworkitems when switch workitem since its already updated
      if (workItems?.length === 0) {
        promises.push(dispatch(getWorkItems()));
      }
      promises.push(
        dispatch(getMarkets({ page: 1, limit: ERecordLength.RL100, workItemId: workItemId })),
        dispatch(
          getFeatures({
            productCode: productCode,
            workItemNum: workItemId,
            includeScopeFeatures: false,
            page: 1,
            limit: ERecordLength.RL100,
          })
        )
      );
    } else {
      dispatch(resetProductData());
      navigate('/');
      return;
    }

    //Call the get market association rule api after changing the WorkItem
    if (!isSaveCalled && activeWorkItem) {
      setPage(1);
      setLimit(ERecordLength.RL100);
      promises.push(
        dispatch(getMarketAssociations(getMarketRuleRequest(activeWorkItem, productCode)))
      );
    }

    // Execute all API calls in parallel
    // Even if one API fails, others will still complete successfully
    await Promise.allSettled(promises);
  };

  useEffect(() => {
    fetchData();
  }, [activeWorkItem?.id]);

  //Once Market data is filled Rows are Created
  useEffect(() => {
    if (markets.length > 0) {
      setPageCount(Math.ceil(markets.length / limit));
      setRows(createRows(markets, 1, limit, product.ma?.updatedMarkets));
    } else {
      setRows([]);
    }
  }, [markets]);

  //Update the rows data whenever updated the product marketAssociations
  useEffect(() => {
    if (product?.ma != null) {
      setRows(createRows(markets, page, limit, product.ma.updatedMarkets));
    }
  }, [product.ma]);

  //Reset the WorkItem after reset all the data
  //Dispatch getworkitems after switch to the new workitem
  useEffect(() => {
    if (isDataReset && newWorkItem) {
      dispatch(setResetDataFlag(false));
      validateWorkItem(newWorkItem);
    }
  }, [isDataReset]);

  //set the selected work item while changing the vlaue in workitem select
  const handleChangeWorkItem = (value: IWorkItemOption) => {
    if (value.id !== activeWorkItem?.id) {
      dispatch(resetSaveStatusOfFeatures());
      dispatch(setNewWorkItem({ id: value.id, name: value.name }));
      if (isDataUpdated) {
        setConfirmWiWarning({
          ...confirmWiWarning,
          open: true,
          description: getWiWarningMessage(value.name),
        });
      } else {
        validateWorkItem({ id: value.id, name: value.name });
      }
    }
  };

  /**
   *This function set the new workitem after all the validations are done
   * @param {IWorkItemOption}value
   */
  const refreshWorkItemData = (value: IWorkItemOption | null) => {
    dispatch(setNewWorkItem(null));
    dispatch(setActiveWorkItem({ id: value?.id, name: value?.name }));
    dispatch(resetIsDataUpdated());
  };

  /**
   * This function dispatch the action to  get the selected workItem details
   * And check whether the status is inprogress else show the invalid workitem error
   * @param {IWorkItemOption} workItem new workitem value
   */
  const validateWorkItem = async (workItem: IWorkItemOption) => {
    if (workItem.id === Master.id) {
      refreshWorkItemData(workItem);
    } else {
      const response = await ProcosApi.getWorkItem(workItem.id, dispatch);
      if (response?.data?.[0].status === EWorkItemStatus.InProgress) {
        refreshWorkItemData(workItem);
      } else {
        dispatch(setNewWorkItem(null));
        dispatch(
          openDialog({
            type: ECommonDialogType.Error,
            data: t('messages.error.WiInvalid'),
          })
        );
      }
    }
  };

  //handle Records Change
  const handleRecordsChange = (pageLimit: number) => {
    const totalPages = Math.ceil(markets.length / pageLimit);
    setPageCount(totalPages);
    setLimit(pageLimit);
    setPage(1);
    setRows(createRows(markets, 1, pageLimit, product.ma?.updatedMarkets));
  };

  //To handle Page change
  const handlePageChange = (currentPage: number) => {
    setRows(createRows(markets, currentPage, limit, product.ma?.updatedMarkets));
    setPage(currentPage);
  };

  const handleConfirmRefresh = () => {
    const workItemId = activeWorkItem && activeWorkItem?.id !== Master.id ? activeWorkItem?.id : '';
    dispatch(getWorkItems());
    dispatch(getMarketAssociations(getMarketRuleRequest(activeWorkItem, productCode)));
    dispatch(
      getFeatures({
        productCode: productCode,
        workItemNum: workItemId,
        includeScopeFeatures: false,
        page: 1,
        limit: ERecordLength.RL100,
      })
    );
    dispatch(resetIsDataUpdated());
  };

  //calls on Refresh button click
  const handleRefresh = () => {
    if (isDataUpdated) {
      dispatch(
        openDialog({
          type: ECommonDialogType.Confirm,
          title: t('dialogBox.unsavedChanges'),
          data: t('messages.refresh'),
          handleClickOk: handleConfirmRefresh,
        })
      );
    } else {
      handleConfirmRefresh();
    }
  };

  //This method triggers when user clicks on cancel or ok button on confirm dialog
  const onConfirmWiStatus = (confirm: IDialogFor) => {
    setConfirmWiWarning({ ...confirmWiWarning, open: false });
    if (confirm.status) {
      dispatch(setResetDataFlag(true));
    } else {
      dispatch(setNewWorkItem(null));
    }
  };

  //Returns the work item warning message with name
  const getWiWarningMessage = (workItemName: string) => {
    return (
      <Trans
        i18nKey="messages.cancel.workitemWarning"
        values={{ workItemName: workItemName }}
        components={{ 1: <b /> }}
      />
    );
  };

  const handleClickSave = async () => {
    try {
      //Create work item only if user editing on Master
      if (!workItem?.id || workItem.id === Master.id) {
        setIsSaveCalled(true);
        const response = await dispatch(createWorkItem(product.id));
        const payload = response.payload as { data?: IWorkItemOption };
        if (payload.data) {
          showWorkItemExecutedToastToUser(payload.data);
          saveEffectivity({ id: payload.data.id, name: payload.data.name });
        }
      } else {
        saveEffectivity(workItem);
      }
    } catch {
      showToastMessage(EMessageType.Error, t('messages.submit.error'), false);
    }
  };

  /**
   * call save Effectivity API and update state values
   * @param {IWorkItemOption} the workitem option
   */
  const saveEffectivity = async ({ id, name }: IWorkItemOption) => {
    try {
      if (id !== Master.id) {
        const effectivityRequest = await getSaveEffectivityRequest(
          { id: id, name: name },
          product,
          procos
        );

        // Call effectivity only if product/features status changed
        if (effectivityRequest.features?.length > 0 || procos.isProductStatusUpdated) {
          const response = await dispatch(createEffectivity(effectivityRequest));
          const payload = response.payload as { data?: IEffectivityResult };
          if (payload.data && !payload.data?.errorId) {
            showEffectivityExecutedToastToUser(payload.data, t);
            saveRules({ id, name });
          }
          //Show Invalid Workitem error if api failed by promoted or invalid workitem
          else if (payload.data?.errorId) {
            dispatch(
              openDialog({
                type: ECommonDialogType.Error,
                data: t('messages.error' + payload.data?.errorId),
              })
            );
          }
        } else {
          saveRules({ id, name });
        }
        /* getFeatures should be called after saving effectivity 
          to get the latest value of variable / feature to show its status in Active column in the UI */
        dispatch(
          getFeatures({
            productCode: productCode,
            workItemNum: id,
            includeScopeFeatures: false,
            page: 1,
            limit: ERecordLength.RL100,
          })
        );
      }
    } catch {
      showToastMessage(EMessageType.Error, t('messages.submit.error'), false);
    }
  };

  // call Create Rules API in batches and update state values
  const saveRules = async ({ id, name }: IWorkItemOption) => {
    try {
      if (id !== Master.id) {
        setIsSaveCalled(false);

        // Save rule only if market rules are available
        const saveRuleRequest = await getSaveRulesRequest({ id: id, name: name }, product);
        if (saveRuleRequest && procos.productStatus === EFeatureStatus.Active) {
          dispatch(resetBatchSaveStatus());
          await handleBatchingForCreateRuleAPI(saveRuleRequest);
        }
      }
    } catch {
      showToastMessage(EMessageType.Error, t('messages.submit.error'), false);
    }
  };

  const handleProcosExport = () => {
    const source: string =
      activeWorkItem && activeWorkItem?.id !== Master.id ? activeWorkItem.id.toString() : '';
    ExportApi.exportProcos({ productId: productCode, source }, dispatch);
  };

  const handlePcsExport = () => {
    ExportApi.exportPcs({ productCode: productCode, packageType: EPackageType.Release }, dispatch);
  };

  return (
    <>
      <Box className="d-flex h-inherit">
        <TabDrawer
          tabsConfig={{
            pathBase: '',
            tabsData: ProcosTabs,
            icons: {
              product: <CategoryIcon className="tab-icon-pdm" />,
              option: <PermDataSettingIcon className="tab-icon-pdm" />,
            },
          }}
        />
        <Box
          id="procos-main"
          className={
            !procos.isCreateRuleInvoked || procos.isBatchSaveComplete
              ? 'enabled h-inherit middle-container'
              : 'disabled h-inherit middle-container'
          }
        >
          <div className="w-100 d-flex justify-spacebetween mb-05em">
            <div className="autocomplete-div d-flex">
              <AutocompleteInput
                data={workItems}
                handleChange={handleChangeWorkItem}
                value={activeWorkItem}
                label={t('labels.workItem')}
                disableClear={true}
                size={EFieldSize.Small}
              />
              <RefreshButton handleRefresh={handleRefresh} />
              {
                <CustomButton
                  label={t('button.proCosExport')}
                  variant="text"
                  disabled={false}
                  type={EPropertyGroup.Download}
                  handleAction={handleProcosExport}
                  startIcon={<Excel />}
                  className="procos-export-button"
                />
              }
              {
                <CustomButton
                  label={t('button.pcsExport')}
                  variant="text"
                  disabled={false}
                  type={EPropertyGroup.Download}
                  handleAction={handlePcsExport}
                  startIcon={<Excel />}
                  className="pcs-export-button"
                />
              }
            </div>
            <ButtonSection handleClickOk={handleClickSave} />
          </div>
          {!isFeaturesPage && (
            <ProCosDataTable
              className="data-table"
              tableType={EDataTableType.Product}
              pageCount={pageCount}
              page={page}
              limit={limit}
              handlePageChange={handlePageChange}
              handleRecordsChange={handleRecordsChange}
              rows={rows}
            />
          )}
          {isFeaturesPage && <FeaturesMarketAssociation />}
        </Box>
      </Box>
      {confirmWiWarning.open && (
        <ConfirmationDialog dialogInfo={confirmWiWarning} onConfirmAction={onConfirmWiStatus} />
      )}
    </>
  );
};
export default Procos;
